import * as i0 from '@angular/core';
import { Directive, Input, TemplateRef, Component, ChangeDetectionStrategy, HostBinding, ContentChild, HostListener, NgModule, EventEmitter } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

class SetAttrsDirective {
    set attrs(value) {
        this._attrs = value;
        this.applyAttributes();
    }
    constructor(el) {
        this.el = el;
        this._attrs = undefined;
        this.applyAttributes();
    }
    applyAttributes() {
        const attrs = this._attrs, el = this.el.nativeElement;
        if (attrs && el) {
            for (const name of Object.keys(attrs)) {
                el.setAttribute(name, attrs[name]);
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SetAttrsDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: SetAttrsDirective, selector: "[ofSetAttrs]", inputs: { attrs: ["ofSetAttrs", "attrs"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SetAttrsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ofSetAttrs]'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { attrs: [{
                type: Input,
                args: ['ofSetAttrs']
            }] } });

/**
 * Simple calculator for a virtual render area
 */
class VirtualRenderArea {
    constructor() {
        this._scrollPos = 0;
        this._viewerHeight = 0;
        this._itemCount = 0;
        this._itemHeight = 0;
        this._visibleCount = 0;
        this._visibleStart = 0;
        this._topBuffer = 0;
        this._totalHeight = 0;
    }
    set itemHeight(value) {
        if (this._itemHeight !== value) {
            this._itemHeight = value;
            this.invalidateViewRange();
            this.invalidateTotalHeight();
            this.invalidateScrollPos();
        }
    }
    get itemHeight() {
        return this._itemHeight;
    }
    set itemCount(value) {
        if (this._itemCount !== value) {
            this._itemCount = value;
            this.invalidateViewRange();
            this.invalidateTotalHeight();
        }
    }
    get itemCount() {
        return this._itemCount;
    }
    set scrollPos(value) {
        if (this._scrollPos !== value) {
            this._scrollPos = value;
            this.invalidateViewRange();
        }
    }
    get scrollPos() {
        return this._scrollPos;
    }
    set viewerHeight(value) {
        if (this._viewerHeight !== value) {
            this._viewerHeight = value;
            this.invalidateViewRange();
            this.invalidateScrollPos();
        }
    }
    get viewerHeight() {
        return this._viewerHeight;
    }
    get visibleCount() {
        return this._visibleCount;
    }
    get visibleStart() {
        return this._visibleStart;
    }
    get topBuffer() {
        return this._topBuffer;
    }
    get totalHeight() {
        return this._totalHeight;
    }
    invalidateViewRange() {
        const maxItems = Math.ceil(this._viewerHeight / this._itemHeight) + 1;
        this._visibleStart = Math.floor(this._scrollPos / this._itemHeight);
        this._visibleCount = Math.min(this._itemCount - this._visibleStart, maxItems);
        this._topBuffer = this._visibleStart * this._itemHeight;
    }
    invalidateTotalHeight() {
        this._totalHeight = this._itemHeight * this._itemCount;
    }
    invalidateScrollPos() {
        this._scrollPos = Math.min(Math.max(0, this._totalHeight - this._viewerHeight), this._scrollPos);
    }
}

class VirtualTreeComponent {
    /**
     * An instance of an OfVirtualTree<T> configured to your dataset
     */
    set model(value) {
        this._model = value;
        this.listenForDataChange(value);
    }
    get model() {
        return this._model;
    }
    /**
     * Height each item in the tree
     */
    set itemHeight(value) {
        this.renderArea.itemHeight = value;
    }
    get itemHeight() {
        return this.renderArea.itemHeight;
    }
    /**
     * @ignore
     */
    get topBuffer() {
        return this.renderArea.topBuffer;
    }
    /**
     * @ignore
     */
    get totalHeight() {
        return this.renderArea.totalHeight;
    }
    constructor(cdr, element) {
        this.cdr = cdr;
        this.element = element;
        this.disposers = [];
        this.renderArea = new VirtualRenderArea();
        /**
         * @ignore
         */
        this.tabIndex = 0;
        /**
         * @ignore
         */
        this.visibleItems = [];
        this.expandAllOnInit = false;
    }
    /**
     * @ignore
     */
    ngAfterViewInit() {
        this.handleDataChange();
        this.invalidateSize();
        this.syncScrollPos();
        if (this.expandAllOnInit) {
            setTimeout(() => {
                this.model.expandAll();
            }, 0);
        }
    }
    /**
     * @ignore
     */
    ngOnDestroy() {
        this.dispose();
    }
    /**
     * @ignore
     */
    handleKeydown(evt) {
        if (evt.key === 'Enter') {
            this.model.selectHighlightedItem();
        }
        else if (evt.key.startsWith('Arrow')) {
            const direction = evt.key.replace('Arrow', ''), nextHighlightedIndex = this.model.navigate(direction);
            if (nextHighlightedIndex !== undefined) {
                this.scrollToIndex(nextHighlightedIndex);
            }
            evt.preventDefault();
        }
    }
    /**
     * @ignore
     */
    handleScrollChange() {
        if (this.element.nativeElement) {
            this.renderArea.scrollPos = this.element.nativeElement.scrollTop;
            this.updateVisibleItems();
        }
    }
    /**
     * @ignore
     */
    handleWindowResize() {
        this.invalidateSize();
    }
    /**
     * Fix issues occurring from tree container's height has changed
     */
    invalidateSize() {
        if (this.element.nativeElement) {
            const bounds = this.element.nativeElement.getBoundingClientRect();
            this.renderArea.viewerHeight = bounds.height;
            if (this.renderArea.itemCount !== this.visibleItems.length) {
                this.updateVisibleItems();
            }
        }
    }
    /**
     * Scroll the container until the selected item is in view. If the selected item is already in view, do nothing.
     */
    scrollToSelected() {
        const selectedIndex = this.model.getSelectedIndex();
        if (typeof selectedIndex === 'number') {
            this.scrollToIndex(selectedIndex);
        }
    }
    /**
     * Scroll to a certain position
     * @param offset In pixels, the scroll position to jump to
     */
    scrollTo(offset) {
        const { nativeElement } = this.element;
        if (nativeElement) {
            nativeElement.scrollTop = offset;
        }
    }
    /**
     * Scroll the container until the item is in view. If the item is already in view, do nothing.
     */
    scrollToItem(item) {
        const selectedIndex = this.model.getItemIndex(item);
        if (typeof selectedIndex === 'number' && selectedIndex > -1) {
            this.scrollToIndex(selectedIndex);
        }
    }
    /**
     * Scroll the container until the item at the index is in view. If the item at the index is already in view, do nothing.
     */
    scrollToIndex(index) {
        const { nativeElement } = this.element;
        if (nativeElement) {
            const { viewerHeight, itemHeight } = this.renderArea, itemTop = index * itemHeight, itemBottom = itemTop + itemHeight;
            if (itemTop < nativeElement.scrollTop) {
                nativeElement.scrollTop = itemTop;
            }
            else if (itemBottom > nativeElement.scrollTop + viewerHeight) {
                nativeElement.scrollTop = itemBottom - viewerHeight;
            }
        }
    }
    /**
     * Get the current scroll offset, pixels
     */
    getScrollPos() {
        return this.renderArea.scrollPos;
    }
    /**
     * Adjust the DOM scroll position to match the VirtualRenderArea scroll postion, and vice versa.
     * This can fix some issues that occur after DOM height changes
     */
    syncScrollPos() {
        const { nativeElement } = this.element;
        if (nativeElement) {
            if (nativeElement.scrollTop !== this.renderArea.scrollPos) {
                nativeElement.scrollTop = this.renderArea.scrollPos;
                this.renderArea.scrollPos = nativeElement.scrollTop;
            }
        }
    }
    listenForDataChange(model) {
        this.dispose();
        const subscription = model.onDataInvalidated.subscribe(() => this.handleDataChange());
        this.disposers.push(() => subscription.unsubscribe());
    }
    dispose() {
        for (const disposer of this.disposers) {
            disposer();
        }
    }
    handleDataChange() {
        this.renderArea.itemCount = this.model.items.length;
        this.updateVisibleItems();
        this.cdr.detectChanges();
    }
    updateVisibleItems() {
        this.visibleItems = this.model.items.slice(this.renderArea.visibleStart, this.renderArea.visibleStart + this.renderArea.visibleCount);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: VirtualTreeComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: VirtualTreeComponent, selector: "virtual-tree", inputs: { model: "model", expandAllOnInit: "expandAllOnInit", itemHeight: "itemHeight" }, host: { listeners: { "keydown": "handleKeydown($event)", "scroll": "handleScrollChange()", "window:resize": "handleWindowResize()" }, properties: { "tabIndex": "this.tabIndex" } }, queries: [{ propertyName: "template", first: true, predicate: TemplateRef, descendants: true }], ngImport: i0, template: `
        <ng-content select="[tree-before]"></ng-content>
        <div class="vt-bottom-space" [style.top.px]="totalHeight"></div>
        <div class="vt-container" [style.top.px]="topBuffer">
            <ng-template ngFor [ngForOf]="visibleItems" [ngForTemplate]="template"></ng-template>
        </div>
        <ng-content select="[tree-after]"></ng-content>
    `, isInline: true, styles: [":host{outline:none;display:block;height:100%;overflow:auto;position:relative}.vt-container{min-width:100%}.vt-container,.vt-bottom-space{position:absolute}.vt-bottom-space{width:1px;height:1px}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: VirtualTreeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'virtual-tree', template: `
        <ng-content select="[tree-before]"></ng-content>
        <div class="vt-bottom-space" [style.top.px]="totalHeight"></div>
        <div class="vt-container" [style.top.px]="topBuffer">
            <ng-template ngFor [ngForOf]="visibleItems" [ngForTemplate]="template"></ng-template>
        </div>
        <ng-content select="[tree-after]"></ng-content>
    `, changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{outline:none;display:block;height:100%;overflow:auto;position:relative}.vt-container{min-width:100%}.vt-container,.vt-bottom-space{position:absolute}.vt-bottom-space{width:1px;height:1px}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }], propDecorators: { tabIndex: [{
                type: HostBinding,
                args: ['tabIndex']
            }], template: [{
                type: ContentChild,
                args: [TemplateRef]
            }], model: [{
                type: Input
            }], expandAllOnInit: [{
                type: Input
            }], itemHeight: [{
                type: Input
            }], handleKeydown: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], handleScrollChange: [{
                type: HostListener,
                args: ['scroll']
            }], handleWindowResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

class VirtualTreeModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: VirtualTreeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: VirtualTreeModule, declarations: [VirtualTreeComponent, SetAttrsDirective], imports: [CommonModule], exports: [VirtualTreeComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: VirtualTreeModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: VirtualTreeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [VirtualTreeComponent, SetAttrsDirective],
                    imports: [CommonModule],
                    exports: [VirtualTreeComponent]
                }]
        }] });

/**
 * Node wraps each item in a hierarchy of data, providing relationship info about the items place in the hierarchy
 */
class Node {
    /**
     * The forward sibling to this node
     */
    get next() {
        return this._next;
    }
    /**
     * Check whether lazy loaded children have been loaded
     */
    get childrenLoaded() {
        return !!this._children;
    }
    /**
     * The children of this node
     */
    get children() {
        return this._children ? this._children : (this._children = this._childrenAccessor());
    }
    /**
     * Bool indicating whether this node has children
     */
    get hasChildren() {
        return !!this.children.length;
    }
    /**
     * Bool indicating whether this is the last among its siblings
     */
    get isLastChild() {
        return !this.next;
    }
    /**
     * Bool indicating whether this is the first among its siblings
     */
    get isFirstChild() {
        return !this.prev;
    }
    /**
     * Bool indicating if this is a root node
     */
    get isRoot() {
        return !this.parent;
    }
    /**
     * Gets the first of this node's children
     */
    get firstChild() {
        return this.children[0];
    }
    /**
     * Gets the last of this node's children
     */
    get lastChild() {
        return this.children[this.children.length - 1];
    }
    /**
     * Creates an iterator of this node's ancestors nearest first
     */
    ancestors() {
        const self = this;
        return (function* () {
            let current = self;
            while ((current = current.parent) !== undefined) {
                yield current;
            }
        })();
    }
    /**
     * Get the root node of this node
     */
    getRoot() {
        let node = this;
        while (node && !node.isRoot) {
            node = node.parent;
        }
        return node;
    }
    /**
     * Get the nearest next sibling among ancestor nodes
     */
    ancestorForward() {
        let result;
        for (const ancestor of this.ancestors()) {
            if (ancestor.next) {
                result = ancestor.next;
                break;
            }
            else if (ancestor.isRoot) {
                result = ancestor;
                break;
            }
        }
        return result;
    }
    /**
     * Get the nearest next node.
     * If this is the last of its siblings, the nearest ancestor's next sibling is returned.
     * If wrap is true and if this is the last node of the tree, the root is next.
     * If wrap is false and if this is the last node of the tree, undefined is next.
     * If next is this node, then undefined is returned.
     * @param wrap if true, then the root node is returned for the last node of the tree
     */
    forwardNode(wrap) {
        let next = this.firstChild || this.next;
        if (!next) {
            next = this.ancestorForward();
        }
        if (next && next.isRoot && !wrap) {
            next = undefined;
        }
        return next === this ? undefined : next;
    }
    reverseNode(wrap) {
        let next = this.prev ? this.prev.lastDescendant() : this.parent;
        if (!next && wrap) {
            const root = this.getRoot();
            next = root ? root.lastDescendant() : undefined;
        }
        return next === this ? undefined : next;
    }
    lastDescendant() {
        let result = this, current = this;
        while ((current = current.lastChild) !== undefined) {
            result = current;
        }
        return result;
    }
    invalidateChildren(reloadNow) {
        this._children = undefined;
        if (reloadNow) {
            this._children = this._childrenAccessor();
        }
    }
    visitSubtree(visitor) {
        visitor(this);
        if (this.childrenLoaded) {
            for (const child of this.children) {
                child.visitSubtree(visitor);
            }
        }
    }
    constructor(
    /**
     * The previous sibling of this node
     */
    prev, 
    /**
     * The parent node of this node
     */
    parent, 
    /**
     * The depth of this node or the number of its ancestors
     */
    depth, 
    /**
     * The data item which this node wraps
     */
    item, 
    /**
     * Index relative to the parent
     */
    index, children) {
        this.prev = prev;
        this.parent = parent;
        this.depth = depth;
        this.item = item;
        this.index = index;
        if (prev) {
            prev._next = this;
        }
        this._childrenAccessor = children;
    }
}

/**
 * Wraps arbitrary hierarchical data in a tree structure with depth, parent, children
 * sibling properties, normalizing hierarchical data so that it can be traversed and
 * queried easily.
 */
class TreeQuery {
    /**
     * Create a tree query, starting with an arbitrary data set, and a delegate for loading child items
     * @param items Any arbitrary array of data
     * @param childAccessor A delegate which, given an arbitrary data item, should return that items children, or undefined if the item has no children
     * @param eagerLoad If true, will descend the entire items hierarchy immediately
     */
    static init(items, childAccessor, eagerLoad = true) {
        const query = new TreeQuery(), rootItem = { items }, accessor = childAccessor || (() => []);
        const createNode = (item, depth, parent, prev, index) => {
            let providedChildren = [];
            const childProvider = () => {
                const childItems = item === rootItem ? rootItem.items : accessor(item), children = [];
                query.cleanLookup(providedChildren);
                if (childItems) {
                    let previous;
                    for (let i = 0; i < childItems.length; i++) {
                        const childItem = childItems[i], 
                        // tslint:disable-next-line: no-use-before-declare
                        child = createNode(childItem, depth + 1, result, previous, i);
                        children.push(child);
                        previous = child;
                    }
                }
                return (providedChildren = children);
            }, result = new Node(prev, parent, depth, item, index || 0, childProvider);
            query.nodeLookup.set(item, result);
            result.invalidateChildren(eagerLoad);
            return result;
        };
        query.rootNode = createNode(rootItem, -1);
        return query;
    }
    constructor(iterator) {
        this.nodeLookup = new Map();
        this.customMove = (_current, def) => def();
        this.iterate = iterator ? iterator : this.forwardIterator();
    }
    extend(iterator) {
        const result = new TreeQuery(iterator);
        result.rootNode = this.rootNode;
        result.nodeLookup = this.nodeLookup;
        return result;
    }
    /**
     * Pagination-like, skip past a number of items. Iterarting will not yield that number of items
     * @param count Number of items to skip through
     */
    skip(count) {
        const iterator = this.iterate;
        return this.extend(function* () {
            let i = 0;
            for (const node of iterator()) {
                if (i >= count) {
                    yield node;
                }
                i++;
            }
        });
    }
    /**
     * Pagination-like, iterate through an exact number of items. Iteration ends after that number is reached
     * @param count Number of items to iterate through
     */
    take(count) {
        const iterator = this.iterate;
        return this.extend(function* () {
            let i = 0;
            for (const node of iterator()) {
                if (i >= count) {
                    break;
                }
                yield node;
                i++;
            }
        });
    }
    /**
     * Apply filter by node, so that only nodes passing the filter are yielded during iteration
     * @param filter A delegate returning bool, true if the node should be iterable
     */
    whereNode(filter) {
        const iterator = this.iterate;
        return this.extend(function* () {
            for (const node of iterator()) {
                if (filter(node)) {
                    yield node;
                }
            }
        });
    }
    /**
     * Apply a filter by item which yields the nodes which have descendants matching the filter
     * @param filter A delegate returning bool, true if its ancestors should be iterable
     * @param excludeMatch False to yield items that match the filter
     */
    hasDescendant(filter, excludeMatch = false) {
        return this.hasDescendantNode(n => filter(n.item), excludeMatch);
    }
    /**
     * Apply a filter by node which yields the nodes which have descendants matching the filter
     * @param filter A delegate returning bool, true if its ancestors should be iterable
     * @param excludeMatch False to yield items that match the filter
     */
    hasDescendantNode(filter, excludeMatch = false) {
        const iterator = this.iterate, self = this;
        return this.extend(function* () {
            const matches = new Set();
            for (const node of self.ascend()) {
                if (!matches.has(node) && filter(node)) {
                    if (!excludeMatch) {
                        matches.add(node);
                    }
                    for (const p of node.ancestors()) {
                        matches.add(p);
                    }
                }
            }
            for (const node of iterator()) {
                if (matches.has(node)) {
                    yield node;
                }
            }
        });
    }
    /**
     * Get the number of items in the hierarchy
     */
    count() {
        let result = 0;
        for (const _node of this.iterate()) {
            result++;
        }
        return result;
    }
    /**
     * Get a single item at the given index
     * @param n The overall index of the item in the hierarchy
     */
    nth(n) {
        const result = this.skip(n)
            .take(1)
            .toArray()
            .find(() => true);
        return result;
    }
    /**
     * Apply filter by item, so that only items passing the filter are yielded during iteration
     * @param filter A delegate returning bool, true if the node should be iterable
     */
    where(filter) {
        return this.whereNode(n => filter(n.item));
    }
    /**
     * Get the first item
     */
    first() {
        for (const result of this) {
            return result.item;
        }
        return undefined;
    }
    /**
     * Visit items in the hierarchy in order from top to bottom regardless of depth
     * @param from Where to start ascending from
     * @param wrap True to start back at the last item after reach the root 0th item
     */
    ascend(from, wrap) {
        const node = (from && this.findNode(from)) || undefined;
        return this.extend(this.reverseIterator(node, wrap));
    }
    /**
     * Depth first, visit items in the hierarchy in order from top to bottom
     * @param from Where to start descending from
     * @param wrap True to start back at the root 0th item after reaching the last item
     */
    descend(from, wrap) {
        const node = (from && this.findNode(from)) || undefined;
        return this.extend(this.forwardIterator(node, wrap));
    }
    /**
     * Control descend behavior and skip iterating branches
     * @param provider A delegate which accepts the current node and should return the node to follow
     */
    provideNext(provider) {
        this.customMove = provider;
        return this;
    }
    /**
     * Control descend behavior and override any preexisting descend behavior
     * @param provider A delegate which accepts the current node and should return the node to follow
     */
    forwardOverride(provider) {
        return this.descend()
            .provideNext(provider)
            .descend();
    }
    /**
     * Convenience method for iterating and returning the iterated items as an array
     */
    toArray() {
        const result = [];
        for (const item of this) {
            result.push(item);
        }
        return result;
    }
    /**
     * @ignore
     */
    [Symbol.iterator]() {
        return this.iterate();
    }
    /**
     * Get the root node of this tree. The root node is a container of other nodes.
     */
    getRootNode() {
        return this.rootNode;
    }
    /**
     * O(1) return the node of the passed item, or undefined if the item is not loaded in this hierarchy
     * @param item
     */
    findNode(item) {
        return this.nodeLookup.get(item);
    }
    reverseIterator(from, wrap) {
        const self = this;
        return function* () {
            const start = from || self.rootNode.lastDescendant();
            let current = start, defaultNext;
            const nextAccessor = () => defaultNext || (defaultNext = current.reverseNode(wrap));
            while (!!current) {
                if (!current.isRoot) {
                    yield current;
                }
                current = self.customMove(current, nextAccessor);
                defaultNext = undefined;
                if (current === start) {
                    break;
                }
            }
        };
    }
    forwardIterator(from, wrap) {
        const self = this;
        return function* () {
            const start = from || self.rootNode;
            let current = start, defaultNext;
            const nextAccessor = () => defaultNext || (defaultNext = current.forwardNode(wrap));
            while (!!current) {
                if (!current.isRoot) {
                    yield current;
                }
                current = self.customMove(current, nextAccessor);
                defaultNext = undefined;
                if (current === start) {
                    break;
                }
            }
        };
    }
    cleanLookup(nodes) {
        for (const node of nodes) {
            node.visitSubtree(n => this.nodeLookup.delete(n.item));
        }
    }
}
/** @ignore */
class TreeConfig {
    constructor(childAccessor, eagerLoad = true) {
        this.childAccessor = childAccessor;
        this.eagerLoad = eagerLoad;
    }
    query(items) {
        return TreeQuery.init(items, this.childAccessor, this.eagerLoad);
    }
}

class VirtualTree {
    get query() {
        return this._query;
    }
    constructor(config, query) {
        this.config = config;
        this.expandedItems = new Set();
        this.onDataInvalidated = new EventEmitter();
        this.onSelectionChanged = new EventEmitter();
        this.onHighlightChanged = new EventEmitter();
        this.items = [];
        if (query) {
            this._query = query;
        }
        else {
            this._query = new TreeConfig(this.config.childAccessor, !this.config.lazyLoad).query([]);
        }
    }
    updateConfig(value) {
        this.config = value;
    }
    isFiltered() {
        return this.filter !== undefined;
    }
    isSelected(item) {
        return this.selectedItem === item;
    }
    isExpanded(item) {
        return this.expandedItems.has(item);
    }
    isHighlighted(item) {
        return this.highlighted === item;
    }
    highlightByIndex(index) {
        const item = index === undefined || this.items[index] === undefined ? undefined : this.items[index].item;
        this.highlight(item);
    }
    getSelectedIndex() {
        const selectedNode = this.selectedItem ? this.query.findNode(this.selectedItem) : undefined, result = selectedNode ? this.items.indexOf(selectedNode) : undefined;
        return result;
    }
    getItemIndex(item) {
        const node = this.query.findNode(item);
        return node ? this.items.indexOf(node) : -1;
    }
    /**
     * Get the TreeQuery's node for the passed item
     * @param item item for which to get the node
     */
    getTreeNode(item) {
        return this.query.findNode(item);
    }
    /**
     * Set the currently highlighted item
     * @param item item to select
     */
    select(item) {
        if (this.selectedItem !== item) {
            this.selectedItem = item;
            this.onSelectionChanged.emit(item);
        }
    }
    /**
     * Set the currently highlighted item
     * @param item item to highlight, undefined to unset the highlighted item
     */
    highlight(item) {
        if (this.highlighted !== item) {
            this.highlighted = item;
            this.onHighlightChanged.emit(item);
        }
    }
    /**
     * Set the passed item as the currently selected and highlighted item
     * @param item item to select & highlight
     */
    selectAndHighlight(item) {
        this.select(item);
        this.highlight(item);
    }
    /**
     * Get the item that is currently selected
     */
    getSelectedItem() {
        return this.selectedItem;
    }
    /**
     * Get the item that is currently highlighted
     */
    getHighlightedItem() {
        return this.highlighted;
    }
    /**
     * Selects the highlighted node, and toggles its expand state
     */
    selectHighlightedItem() {
        let didChange = false;
        if (this.highlighted) {
            this.toggle(this.highlighted);
            didChange = this.selectedItem !== this.highlighted;
            this.select(this.highlighted);
        }
        return didChange;
    }
    /**
     * Expand all ancestry of the currently selected item
     */
    expandToSelectedItem() {
        if (this.selectedItem) {
            this.expandToItem(this.selectedItem);
            this.invalidateData();
        }
    }
    /**
     * Expand all ancestry of the passed item
     * @param item item to expand to
     */
    expandToItem(item) {
        const node = this.query.findNode(item);
        if (node) {
            for (const parent of node.ancestors()) {
                if (!parent.isRoot) {
                    this.expandedItems.add(parent.item);
                }
            }
        }
    }
    /**
     * Toggle the expanded state of the passed item
     * @param item item to toggle
     */
    toggle(item) {
        if (this.isExpandable(item)) {
            if (this.isExpanded(item)) {
                this.expandedItems.delete(item);
            }
            else {
                this.expandedItems.add(item);
            }
            this.invalidateData();
        }
    }
    /**
     * Set the expanded state of an item. This does nothing if the item is not expandable or already in the desired state
     * This does not update the UI. Expect to call invalidateData/invalidateItem to see the effect.
     * @param item item to expanded or collapse
     * @param expanded true to expand, false to collapse
     */
    setExpanded(item, expanded) {
        if (this.isExpandable(item)) {
            const isExpanded = this.isExpanded(item);
            if (isExpanded && !expanded) {
                this.expandedItems.delete(item);
            }
            else if (!isExpanded && expanded) {
                this.expandedItems.add(item);
            }
        }
    }
    expandAll() {
        for (const node of this.query) {
            if (this.isExpandable(node.item)) {
                this.expandedItems.add(node.item);
            }
        }
        this.invalidateData();
    }
    collapseAll() {
        this.expandedItems.clear();
        this.invalidateData();
    }
    /**
     * Apply a filter that ignore expand state and makes visible all nodes that either
     * match the predicate or contain a child that matches the predicate
     * @param filter predicate, true to make a node visible
     */
    setFilter(filter) {
        this.filter = filter;
        this.invalidateData();
    }
    /**
     * Returns true if the passed item is expandable
     * @param item item to check
     */
    isExpandable(item) {
        let result = false;
        if (!this.filter && this.config.canExpand) {
            result = this.config.canExpand(item);
        }
        else {
            const node = this.query.findNode(item);
            result = !!node && node.hasChildren;
        }
        return result;
    }
    /**
     * Update data from the root
     * @param items Data to load
     */
    load(items) {
        this._query = new TreeConfig(this.config.childAccessor, !this.config.lazyLoad).query(items);
        this.invalidateData();
    }
    /**
     * Signal children to be reloaded for a particular node.
     * Has no effect if the passed parent has not been loaded
     * @param parent item to reload
     */
    reloadChildren(parent) {
        const node = parent ? this.query.findNode(parent) : this.query.getRootNode();
        if (node) {
            node.invalidateChildren(!this.config.lazyLoad);
            this.invalidateData();
        }
    }
    /**
     * Signal the tree to reload all data
     */
    reloadTree() {
        const root = this.query.getRootNode();
        root.invalidateChildren(!this.config.lazyLoad);
        this.invalidateData();
    }
    /**
     * Change the highlighted node by the given direction.
     * 'Left' will collapse before navigation.
     * 'Right' will expand before navigation.
     * @param direction 'Left' | 'Up' | 'Down' | 'Right'
     */
    navigate(direction) {
        const selectedNode = this.highlighted ? this.query.findNode(this.highlighted) : undefined, index = selectedNode ? this.items.indexOf(selectedNode) : undefined;
        let nextSelectedIndex;
        if (direction === 'Up') {
            nextSelectedIndex = index === undefined ? this.items.length - 1 : index === 0 ? undefined : index - 1;
        }
        else if (direction === 'Down' ||
            (direction === 'Right' && this.highlighted && (!this.isExpandable(this.highlighted) || this.isExpanded(this.highlighted)))) {
            nextSelectedIndex = index === undefined ? 0 : index === this.items.length - 1 ? undefined : index + 1;
        }
        else if (direction === 'Left' && selectedNode) {
            if (this.highlighted && this.isExpanded(this.highlighted)) {
                this.toggle(this.highlighted);
                nextSelectedIndex = index;
            }
            else if (selectedNode.parent) {
                nextSelectedIndex = this.items.indexOf(selectedNode.parent);
            }
        }
        else if (direction === 'Right') {
            if (this.highlighted && !this.isExpanded(this.highlighted)) {
                this.toggle(this.highlighted);
                nextSelectedIndex = index;
            }
        }
        this.highlightByIndex(nextSelectedIndex);
        return nextSelectedIndex;
    }
    /**
     * Signal that the state has changed and the tree needs to be revisited
     */
    invalidateData() {
        if (this.filter) {
            this.items = this.query
                .descend()
                .hasDescendant(this.filter)
                .toArray();
        }
        else {
            this.items = this.query
                .forwardOverride((curr, fallback) => !curr.isRoot && !this.isExpanded(curr.item) ? curr.next || curr.ancestorForward() : fallback())
                .whereNode(n => this.isExpanded(n.parent.item) || n.parent.isRoot)
                .toArray();
        }
        this.onDataInvalidated.emit();
    }
    /**
     * Signal that the children of a node have changed and the node needs to have the children reloaded
     * @param item item whose children should be reloaded
     * @param reloadImmediately true to update children immediate, not on-demand
     */
    invalidateItem(item, reloadImmediately = true) {
        if (item) {
            const node = this.getTreeNode(item);
            if (node) {
                node.invalidateChildren(reloadImmediately);
                this.invalidateData();
            }
        }
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { Node, SetAttrsDirective, TreeConfig, TreeQuery, VirtualRenderArea, VirtualTree, VirtualTreeComponent, VirtualTreeModule };
